/**
 * Briefing-tab signal grouping: love / know / value.
 *
 * Used by Q2 (what's working — `love`), Q3 (what's broken — `know`) and Q5
 * (the price/value receipts — `value`). This is a flat list, NOT the Explore
 * tab grouping (see `themes.ts` for that).
 *
 * `love`  — strength signals: the things customers feel when they DO know you.
 * `know`  — visibility / trust gap: the things broken in the open market.
 * `value` — pricing & value perception (price level, deals, advertising).
 *
 * Some entries appear in both `themes.ts` and here — the two groupings are
 * orthogonal (themes = areas a marketer manages; groups = Briefing framing).
 *
 * Names below are CANONICAL — Apply `getDisplayName` at render time so e.g.
 * "Alignment Depth" → "Touchpoints", "Standardised NPS" → "Advocacy Intent".
 */

import type { ScoreGroup } from '../types';

const LOVE_SIGNALS: readonly string[] = Object.freeze([
  'Self Congruence',
  'Brand Valence',
  'User Congruence',
  'Product Performance',
  'Product Richness',
  'Standardised NPS', // displayed as "Advocacy Intent"
  'Brand Empathy',
  'Repurchase Intention',
  'Prominence',
  'Uniqueness',
  'Staff Behaviour',
  'Self Reported Loyalty',
  'Brand Identification',
]);

const KNOW_SIGNALS: readonly string[] = Object.freeze([
  'Associations',
  'Alignment Depth', // displayed as "Touchpoints"
  'Symbolic Recall',
  'Brand Self Connection',
  'Brand Familiarity',
  'Brand Trust',
  'Distribution Intensity', // displayed as "Availability"
  'Brand Environment',
  'Parity Preference',
  'Price Deals',
  'Market Awareness',
  'Unprompted Brand Awareness',
  'Prompted Brand Awareness',
]);

const VALUE_SIGNALS: readonly string[] = Object.freeze([
  'Quality Value Tradeoff (PQV)',
  'Perceived Value for the Cost',
  'Price',
  'Advertising Intensity',
  'Advertising Content',
]);

const GROUP_BY_CANONICAL: Readonly<Record<string, ScoreGroup>> = Object.freeze(
  {
    ...Object.fromEntries(LOVE_SIGNALS.map((n) => [n, 'love' as ScoreGroup])),
    ...Object.fromEntries(KNOW_SIGNALS.map((n) => [n, 'know' as ScoreGroup])),
    ...Object.fromEntries(VALUE_SIGNALS.map((n) => [n, 'value' as ScoreGroup])),
  },
);

/** Return the love/know/value group for a canonical signal name. */
export function groupForSignal(canonical: string): ScoreGroup {
  // Default to 'know' so unfamiliar canonical names still render somewhere
  // rather than being silently dropped — easier to spot regressions.
  return GROUP_BY_CANONICAL[canonical] ?? 'know';
}

export const SIGNAL_GROUPS = Object.freeze({
  love: LOVE_SIGNALS,
  know: KNOW_SIGNALS,
  value: VALUE_SIGNALS,
});
